import {
  CONSENT_POLICY_VERSION,
  type ConsentRecord,
  type OptionalConsentCategory,
} from "./consentTypes";

/**
 * Where the consent decision lives, and how the traces of a withdrawn one are removed.
 *
 * <p>The consent cookie is itself in the NECESSARY category: it exists only to remember that the
 * visitor was asked, so it is set without consent and survives a withdrawal. It is first-party,
 * host-only (no Domain attribute) and carries no identifier — the receipt id is random and is not
 * used for anything but matching a receipt to a support question.</p>
 *
 * <p>Every entry point is total: cookies can be blocked outright, and <c>document.cookie</c> throws
 * rather than returning "" in a sandboxed frame. A failed read is "no consent", never a grant, and a
 * failed write falls back to memory so the current tab still honours the choice.</p>
 */

/** Name of the first-party consent cookie. */
export const CONSENT_COOKIE_NAME = "lob_consent";

/** Default lifetime, 12 months — the outer limit a consent should be relied on before re-asking. */
export const CONSENT_COOKIE_MAX_AGE_DAYS = 365;

const SECONDS_PER_DAY = 24 * 60 * 60;
const MS_PER_DAY = SECONDS_PER_DAY * 1000;

/**
 * The decision when the cookie could not be written. Strictly a fallback: while a cookie holds the
 * record this stays null, so clearing site data from devtools genuinely revokes instead of being
 * papered over by a stale copy in this module.
 */
let memoryRecord: ConsentRecord | null = null;

// ── Cookie primitives ────────────────────────────────────────────────────────

function readRawCookie(name: string): string | null {
  try {
    const prefix = `${name}=`;
    for (const part of document.cookie.split(";")) {
      const entry = part.trim();
      if (entry.startsWith(prefix)) return decodeURIComponent(entry.slice(prefix.length));
    }
  } catch {
    // Storage disabled or a sandboxed frame — the getter itself throws.
  }
  return null;
}

function visibleCookieNames(): string[] {
  try {
    return document.cookie
      .split(";")
      .map((part) => part.split("=")[0].trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

function expireCookie(name: string, domain: string | null, path: string): void {
  try {
    // Both Max-Age and Expires: a browser that ignores one honours the other.
    document.cookie =
      `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}` +
      (domain ? `; Domain=${domain}` : "");
  } catch {
    // Cookies blocked, so there was nothing to expire either.
  }
}

function isHttps(): boolean {
  try {
    return location.protocol === "https:";
  } catch {
    return false;
  }
}

// ── Reading and writing the decision ─────────────────────────────────────────

/**
 * Turn a raw cookie value into a record, or null. Anything unexpected — a bumped policy version, an
 * unparseable body, a missing category, a timestamp beyond the lifetime — resolves to null, which the
 * store reads as "ask again".
 */
function parseRecord(raw: string | null): ConsentRecord | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ConsentRecord> | null;
    if (!parsed || typeof parsed !== "object") return null;

    // The whole point of the version: a record written against older wording is not consent to the
    // current one, however recent it is. The constant is the floor this build ships; the tenant's own
    // version, which may be higher, is checked by the store once the configuration has arrived.
    if (typeof parsed.policyVersion !== "number" || parsed.policyVersion < CONSENT_POLICY_VERSION) return null;

    const categories = parsed.categories;
    if (
      !categories ||
      typeof categories.statistics !== "boolean" ||
      typeof categories.marketing !== "boolean"
    ) {
      return null;
    }

    const decidedAt = typeof parsed.decidedAt === "string" ? parsed.decidedAt : "";
    const decidedMs = Date.parse(decidedAt);
    if (Number.isNaN(decidedMs)) return null;

    // The cookie normally expires on its own; this catches a record that outlived its own lifetime
    // (an extended cookie, or the in-memory fallback of a very long-lived tab).
    if (Date.now() - decidedMs > CONSENT_COOKIE_MAX_AGE_DAYS * MS_PER_DAY) return null;

    return {
      policyVersion: parsed.policyVersion,
      decidedAt,
      categories: {
        necessary: true,
        statistics: categories.statistics,
        marketing: categories.marketing,
      },
      tenant: typeof parsed.tenant === "string" ? parsed.tenant : "",
      hostname: typeof parsed.hostname === "string" ? parsed.hostname : "",
      source: parsed.source === "settings" ? "settings" : "banner",
      consentReceiptId: typeof parsed.consentReceiptId === "string" ? parsed.consentReceiptId : "",
    };
  } catch {
    return null;
  }
}

/** The decision on file, or null when there is none this policy version can rely on. */
export function readConsent(): ConsentRecord | null {
  return parseRecord(readRawCookie(CONSENT_COOKIE_NAME)) ?? memoryRecord;
}

function writeCookie(record: ConsentRecord, maxAgeDays: number): boolean {
  const value = encodeURIComponent(JSON.stringify(record));
  const maxAge = Math.max(0, Math.round(maxAgeDays * SECONDS_PER_DAY));

  // No Domain attribute — host-only, so a decision made on one white-label host never silently
  // covers another brand on a sibling host.
  // Secure only over https: a Secure cookie set over plain http is dropped without a word, which
  // would make the banner reappear on every page load of the http dev server.
  const secure = isHttps() ? "; Secure" : "";

  try {
    document.cookie =
      `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  } catch {
    return false;
  }

  // Assignment is silent when the cookie is refused (blocked, or past the 4 KB limit), so the only
  // way to know it landed is to read it back.
  return readRawCookie(CONSENT_COOKIE_NAME) !== null;
}

/**
 * Persist a decision. Returns whether it actually reached a cookie; false means it is held in memory
 * for this tab only and the visitor will be asked again on the next load.
 */
export function writeConsent(
  record: ConsentRecord,
  maxAgeDays: number = CONSENT_COOKIE_MAX_AGE_DAYS,
): boolean {
  const persisted = writeCookie(record, maxAgeDays);
  memoryRecord = persisted ? null : record;
  return persisted;
}

/** Forget the decision entirely, so the visitor is asked again rather than left on a stale grant. */
export function clearConsent(): void {
  memoryRecord = null;
  expireCookie(CONSENT_COOKIE_NAME, null, "/");
}

// ── Clearing what a withdrawn category left behind ───────────────────────────

/**
 * The cookies the vendors we can be configured with are known to set. <c>exact</c> entries are deleted
 * blind (they may have been set on a wider domain than this document can see); prefix entries can only
 * be matched against what is visible, since their suffix is per-property.
 */
const TRACKED_COOKIES: { category: OptionalConsentCategory; match: string; exact: boolean }[] = [
  { category: "statistics", match: "_ga", exact: true }, // GA4 client id
  { category: "statistics", match: "_ga_", exact: false }, // GA4 session state, per measurement id
  { category: "statistics", match: "_gid", exact: true },
  { category: "marketing", match: "_gcl_", exact: false }, // Google Ads click identifiers
  { category: "marketing", match: "_fbp", exact: true }, // Meta browser id
  { category: "marketing", match: "_fbc", exact: true }, // Meta click id
  { category: "marketing", match: "_ttp", exact: true }, // TikTok pixel id
  { category: "marketing", match: "_tt_", exact: false },
];

/** Key prefixes the same vendors use in localStorage when a cookie is unavailable to them. */
const TRACKED_STORAGE_PREFIXES: { category: OptionalConsentCategory; prefix: string }[] = [
  { category: "statistics", prefix: "_ga" },
  { category: "marketing", prefix: "_gcl" },
  { category: "marketing", prefix: "_fb" },
  { category: "marketing", prefix: "_tt" },
];

/**
 * The brand apex, tried explicitly: a tag loaded on a sub-domain may have widened its cookie to
 * ".lobas.no". The browser rejects the attempt when the page is on another registrable domain, which
 * is precisely the behaviour we want — no cross-brand reach.
 */
const EXTRA_DELETION_DOMAINS = [".lobas.no"];

/**
 * Every Domain attribute a tag might plausibly have used, so the delete lands on the cookie that
 * actually exists. A cookie can only be removed with the same domain and path it was set with, and
 * <c>document.cookie</c> does not tell us which those were.
 */
function deletionDomains(): (string | null)[] {
  const domains: (string | null)[] = [null]; // host-only: no Domain attribute at all

  let host = "";
  try {
    host = location.hostname;
  } catch {
    // No location (or blocked) — the host-only attempt above is all we can do.
  }

  if (host) {
    domains.push(host);
    // Each parent domain up to but not including the TLD: "shop.lobas.no" also tries ".lobas.no".
    const parts = host.split(".");
    for (let i = 0; i + 2 <= parts.length; i++) {
      const parent = parts.slice(i).join(".");
      if (parent.includes(".")) domains.push(`.${parent}`);
    }
  }

  domains.push(...EXTRA_DELETION_DOMAINS);
  return [...new Set(domains)];
}

/**
 * Delete the cookies and storage keys belonging to the given categories.
 *
 * <p>Called both on withdrawal and when a single category is switched off from the settings panel:
 * turning statistics off while marketing stays on must still remove the GA cookies, or the visitor's
 * refusal only stops new writes and leaves the old identifier in place.</p>
 */
export function clearTrackingStorage(categories: readonly OptionalConsentCategory[]): void {
  if (categories.length === 0) return;

  const visible = visibleCookieNames();
  const names = new Set<string>();

  for (const entry of TRACKED_COOKIES) {
    if (!categories.includes(entry.category)) continue;
    if (entry.exact) {
      names.add(entry.match);
    } else {
      for (const name of visible) if (name.startsWith(entry.match)) names.add(name);
    }
  }

  const domains = deletionDomains();
  for (const name of names) {
    for (const domain of domains) expireCookie(name, domain, "/");
  }

  const prefixes = TRACKED_STORAGE_PREFIXES.filter((p) => categories.includes(p.category)).map(
    (p) => p.prefix,
  );
  if (prefixes.length === 0) return;

  try {
    // Collect first: removeItem while iterating by index shifts the keys underneath us.
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) doomed.push(key);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    // Storage disabled — nothing was written there in the first place.
  }
}
